import { colors } from "@styles/colors";
import { useEffect, useRef, useState } from "react";
import DraftNotFoundView from "./components/DraftNotFoundView";

/* ───── Icons ───── */
const CheckIcon = ({ size = 64 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="32" fill="#22C55E" />
    <path d="M20 33L28 41L44 23" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="8.5" stroke="#2563eb" strokeWidth="1.5" />
    <path d="M10 6V10.5L13 13" stroke="#2563eb" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const LocationIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path d="M10 2C6.68 2 4 4.68 4 8C4 12.5 10 18 10 18S16 12.5 16 8C16 4.68 13.32 2 10 2ZM10 10.5C8.62 10.5 7.5 9.38 7.5 8 7.5 6.62 8.62 5.5 10 5.5S12.5 6.62 12.5 8C12.5 9.38 11.38 10.5 10 10.5Z" fill="#2563eb" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path d="M4.5 3C4.22 3 4 3.22 4 3.5 4 11.51 10.49 18 18.5 18 18.78 18 19 17.78 19 17.5V14.5C19 14.22 18.78 14 18.5 14 17.12 14 15.79 13.75 14.56 13.3 14.45 13.26 14.33 13.27 14.22 13.33L12.21 15.34C9.38 13.88 7.12 11.62 5.66 8.79L7.67 6.78C7.73 6.67 7.74 6.55 7.7 6.44 7.25 5.21 7 3.88 7 2.5 7 2.22 6.78 2 6.5 2H3.5C3.22 2 3 2.22 3 2.5" fill="#5B8DEF" />
  </svg>
);

const NoteIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <rect x="3" y="2" width="14" height="16" rx="2" stroke="#A78BFA" strokeWidth="1.5" fill="none" />
    <path d="M7 7H13M7 10H13M7 13H10" stroke="#A78BFA" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const GasIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <rect x="5" y="4" width="10" height="13" rx="2" fill="#2563eb" />
    <rect x="7" y="6" width="6" height="4" rx="1" fill="#F0F4FF" />
    <circle cx="10" cy="3" r="1.5" fill="#2563eb" />
    <rect x="8" y="17" width="4" height="1" rx="0.5" fill="#2563eb" />
  </svg>
);

const ReceiptIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
    <path d="M5 3C5 2.448 5.448 2 6 2H14C14.552 2 15 2.448 15 3V17L12.5 15.5 10 17 7.5 15.5 5 17V3Z" stroke="#F59E0B" strokeWidth="1.5" fill="none" />
    <path d="M8 6H12M8 9H12M8 12H10" stroke="#F59E0B" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const ArrowLeft = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SpinnerIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ animation: "spin 0.8s linear infinite" }}>
    <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
    <path d="M12 2A10 10 0 0 1 22 12" stroke="white" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const OrderExpiredImg = require("@styles/images/order_expired.png") as string;

/* ───── Confetti ───── */
const ConfettiParticle = ({
  delay,
  x,
  color,
  size,
}: {
  delay: number;
  x: number;
  color: string;
  size: number;
}) => (
  <div
    style={{
      position: "absolute",
      left: `${x}%`,
      top: "-10px",
      width: `${size}px`,
      height: `${size}px`,
      backgroundColor: color,
      borderRadius: size > 6 ? "2px" : "50%",
      animation: `confettiFall 2.5s ease-in ${delay}s forwards`,
      opacity: 0,
      transform: `rotate(${Math.random() * 360}deg)`,
    }}
  />
);

/* ───── Shared components ───── */
function SectionCard({
  icon,
  title,
  iconBg,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  iconBg: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        padding: "18px 20px",
        marginBottom: 12,
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        border: "1px solid #F5F5F5",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </div>
        <span style={{ fontSize: 16, fontWeight: 700, color: "#333" }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function InfoRow({
  label,
  value,
  muted,
}: {
  label: string;
  value: React.ReactNode;
  muted: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 0",
        borderBottom: "1px solid #FAFAFA",
      }}
    >
      <span style={{ fontSize: 13, color: "#999", flexShrink: 0 }}>{label}</span>
      <span
        style={{
          fontSize: 14,
          color: muted ? "#CCC" : "#333",
          fontWeight: muted ? 400 : 500,
          textAlign: "right",
          marginLeft: 12,
        }}
      >
        {value}
      </span>
    </div>
  );
}

/* ───── View Model ───── */
export type ConfirmOrderViewModel = {
  orderId?: string | null;
  createdAt?: string | null;
  items: { name: string; weight: string; type: string; qty: number }[];
  delivery: {
    address: string;
    method: string;
    estimatedTime: string;
    note?: string;
  };
  contact: { phone: string };
  invoice: { taxId?: string; carrier?: string };
  note?: string;
  businessHours?: string;
};

type Step = "review" | "submitting" | "success" | "expired";

interface OrderConfirmFlowProps {
  onBack: () => void;
  onConfirm: () => Promise<void> | void;
  order: ConfirmOrderViewModel;
  onBackToLine: () => void;
}

function isDraftOrderExpiredError(err: unknown): boolean {
  const response = (err as any)?.response;
  const responseStatus = response?.status;
  const bodyStatus = response?.data?.status ?? response?.data?.statusCode;
  const rawMessage = response?.data?.message ?? (err as any)?.message ?? "";
  const message = String(rawMessage);

  return (
    responseStatus === 404 ||
    bodyStatus === 404 ||
    message.includes("Draft order not found") ||
    message.includes("找不到草稿")
  );
}

/* ===== MAIN COMPONENT ===== */
export default function OrderConfirmFlow({
  onBack,
  onConfirm,
  order,
  onBackToLine,
}: OrderConfirmFlowProps) {
  const [step, setStep] = useState<Step>("review");
  const [showContent, setShowContent] = useState(false);
  const [copied, setCopied] = useState(false);
  const topRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setShowContent(true), 200);
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = async () => {
    if (step === "submitting") return;
    setStep("submitting");
    try {
      await onConfirm();
      setStep("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (isDraftOrderExpiredError(err)) {
        setStep("expired");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      setStep("review");
    }
  };

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (step === "success") {
    return (
      <SuccessPage
        order={order}
        copied={copied}
        onCopy={handleCopy}
        onBackToLine={onBackToLine}
      />
    );
  }

  if (step === "expired") {
    return <ExpiredPage onBackToLine={onBackToLine} />;
  }

  /* ───── STEP 1: Review / Confirm ───── */
  return (
    <div
      ref={topRef}
      style={{
        minHeight: "100dvh",
        maxHeight: "100dvh",
        background: "linear-gradient(180deg, #F0F4FF 0%, #FFFFFF 20%)",
        fontFamily: "var(--font-sans)",
        width: "100%",
        position: "relative",
        paddingBottom: 110,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <style>
        {`
        @keyframes slideUp { 0%{transform:translateY(24px);opacity:0} 100%{transform:translateY(0);opacity:1} }
        @keyframes spin { to{transform:rotate(360deg)} }
      `}
      </style>

      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
          padding: "0 0 24px",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.06,
            backgroundImage:
              "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        {/* Top bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "14px 16px",
            position: "relative",
            zIndex: 2,
          }}
        >
          <button
            type="button"
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: 10,
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <ArrowLeft />
          </button>
          <span
            style={{
              flex: 1,
              textAlign: "center",
              color: "#fff",
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          >
            雲端桶裝瓦斯服務
          </span>
          <div style={{ width: 36 }} />
        </div>
        {/* Title */}
        <div
          style={{
            textAlign: "center",
            padding: "8px 24px 0",
            position: "relative",
            zIndex: 2,
          }}
        >
          <h1
            style={{
              color: "#fff",
              fontSize: 22,
              fontWeight: 800,
              margin: "0 0 6px",
            }}
          >
            確認訂單內容
          </h1>
          <p
            style={{
              color: "rgba(255,255,255,0.8)",
              fontSize: 13,
              margin: 0,
            }}
          >
            請確認以下修改內容無誤後送出
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div
        style={{
          margin: "-12px 16px 16px",
          background: "#fff",
          borderRadius: 14,
          padding: "14px 20px",
          boxShadow: "0 4px 16px rgba(43, 132, 233,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0,
          animation: showContent ? "slideUp 0.4s ease forwards" : "none",
          opacity: showContent ? 1 : 0,
        }}
      >
        <StepDot done label="修改訂單" num="✓" />
        <div
          style={{
            width: 40,
            height: 2,
            background: colors.primary,
            margin: "0 4px",
            marginBottom: 18,
          }}
        />
        <StepDot active current label="確認內容" num="2" />
        <div
          style={{
            width: 40,
            height: 2,
            background: "#E5E7EB",
            margin: "0 4px",
            marginBottom: 18,
          }}
        />
        <StepDot label="完成" num="3" />
      </div>

      {/* Content */}
      <div
        style={{
          padding: "0 16px",
          animation: showContent ? "slideUp 0.4s ease 0.15s forwards" : "none",
          opacity: showContent ? 1 : 0,
        }}
      >
        {/* Gas Items */}
        <SectionCard icon={<GasIcon />} title="瓦斯品項 / 數量" iconBg="#F0F4FF">
          {order.items.map((item, idx) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 0",
                borderBottom: idx < order.items.length - 1 ? "1px solid #F3F4F6" : "none",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: "linear-gradient(135deg, #F0F4FF, #d3ddf7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    color: colors.primary,
                  }}
                >
                  {item.weight}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    {item.name}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#999",
                      marginTop: 2,
                    }}
                  >
                    {item.type}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: colors.primary,
                  }}
                >
                  ×{item.qty}
                </div>
              </div>
            </div>
          ))}
        </SectionCard>

        {/* Delivery */}
        <SectionCard icon={<LocationIcon />} title="配送資訊" iconBg="#F0F4FF">
          <InfoRow label="配送地址" value={order.delivery.address} muted={false} />
          <InfoRow
            label="配送方式"
            value={
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <ClockIcon />
                <span style={{ color: colors.primary, fontWeight: 600 }}>{order.delivery.method}</span>
              </span>
            }
            muted={false}
          />
          <InfoRow
            label="預計時間"
            value={
              <span>
                <span style={{ fontWeight: 600 }}>{order.delivery.estimatedTime}</span>
                {order.delivery.note && (
                  <span style={{ fontSize: 12, color: "#999", marginLeft: 4 }}>({order.delivery.note})</span>
                )}
              </span>
            }
            muted={false}
          />
        </SectionCard>

        {/* Contact */}
        <SectionCard icon={<PhoneIcon />} title="聯絡電話" iconBg="#EFF6FF">
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: "#333",
              letterSpacing: 1,
            }}
          >
            {order.contact.phone}
          </div>
        </SectionCard>

        {/* Invoice */}
        <SectionCard icon={<ReceiptIcon />} title="統編 / 載具" iconBg="#FFFBEB">
          <InfoRow
            label="統一編號"
            value={order.invoice.taxId || "未填寫"}
            muted={!order.invoice.taxId}
          />
          <InfoRow
            label="電子發票載具"
            value={order.invoice.carrier || "未填寫"}
            muted={!order.invoice.carrier}
          />
        </SectionCard>

        {/* Notes */}
        <SectionCard icon={<NoteIcon />} title="訂單備注" iconBg="#F3F0FF">
          <div
            style={{
              fontSize: 14,
              color: order.note ? "#333" : "#CCC",
              fontStyle: order.note ? "normal" : "italic",
            }}
          >
            {order.note || "無備注"}
          </div>
        </SectionCard>

        {/* Business hours */}
        <div
          style={{
            margin: "4px 0 16px",
            padding: "14px 18px",
            background: "linear-gradient(135deg, #F8FAFC, #F1F5F9)",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            gap: 10,
            border: "1px solid #E2E8F0",
          }}
        >
          <span style={{ fontSize: 16 }}>🕐</span>
          <span style={{ fontSize: 13, color: "#64748B", fontWeight: 500 }}>
            營業時間：{order.businessHours || "依瓦斯行實際營業時間為準"}
          </span>
        </div>
      </div>

      {/* Bottom buttons */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          background: "#fff",
          borderTop: "1px solid #F0F0F0",
          padding: "12px 16px",
          paddingBottom: "max(12px, env(safe-area-inset-bottom))",
          display: "flex",
          gap: 12,
          zIndex: 20,
          boxSizing: "border-box",
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            flex: 1,
            padding: "15px",
            borderRadius: 12,
            border: "2px solid #E5E7EB",
            background: "#fff",
            color: "#666",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          返回修改
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={step === "submitting"}
          style={{
            flex: 1.5,
            padding: "15px",
            borderRadius: 12,
            border: "none",
            background:
              step === "submitting"
                ? "linear-gradient(135deg, #F0F4FF, #d3ddf7)"
                : "linear-gradient(135deg, #2563eb, #2563eb)",
            color: "#fff",
            fontSize: 16,
            fontWeight: 700,
            cursor: step === "submitting" ? "default" : "pointer",
            boxShadow: "0 4px 14px rgba(43, 132, 233, 0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            transition: "all 0.3s",
          }}
        >
          {step === "submitting" ? (
            <>
              <SpinnerIcon /> 送出中...
            </>
          ) : (
            "確認送出"
          )}
        </button>
      </div>
    </div>
  );
}

/* ───── Step Dot ───── */
function StepDot({
  active,
  current,
  done,
  label,
  num,
}: {
  active?: boolean;
  current?: boolean;
  done?: boolean;
  label: string;
  num: string;
}) {
  const bg = done ? colors.primary : active ? colors.primary : "#E5E7EB";
  const color = done || active ? "#fff" : "#999";
  const labelColor = done || active ? colors.primary : "#999";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: bg,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          boxShadow: current ? "0 0 0 4px rgba(37, 99, 235,0.2)" : "none",
          transition: "all 0.3s",
        }}
      >
        {num}
      </div>
      <span
        style={{
          fontSize: 11,
          color: labelColor,
          fontWeight: current ? 700 : 500,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    </div>
  );
}

/* ===== SUCCESS PAGE ===== */
function SuccessPage({
  order,
  copied,
  onCopy,
  onBackToLine,
}: {
  order: ConfirmOrderViewModel;
  copied: boolean;
  onCopy: () => void;
  onBackToLine: () => void;
}) {
  const [show, setShow] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 300);
    const t2 = setTimeout(() => setShowConfetti(false), 3200);
    return () => {
      clearTimeout(t);
      clearTimeout(t2);
    };
  }, []);

  const confettiColors = ["#FF6B35", "#22C55E", "#3B82F6", "#F59E0B", "#EC4899", "#8B5CF6"];
  const particles = Array.from({ length: 30 }, () => ({
    delay: Math.random() * 0.8,
    x: Math.random() * 100,
    color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
    size: 4 + Math.random() * 6,
  }));

  return (
    <div
      style={{
        minHeight: "100dvh",
        maxHeight: "100dvh",
        background: "linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 30%)",
        fontFamily: "var(--font-sans)",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        paddingBottom: 110,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <style>
        {`
        @keyframes confettiFall { 0%{opacity:1;transform:translateY(0) rotate(0deg)} 100%{opacity:0;transform:translateY(600px) rotate(720deg)} }
        @keyframes scaleIn { 0%{transform:scale(0);opacity:0} 60%{transform:scale(1.15)} 100%{transform:scale(1);opacity:1} }
        @keyframes slideUp { 0%{transform:translateY(24px);opacity:0} 100%{transform:translateY(0);opacity:1} }
        @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.05)} }
      `}
      </style>

      {/* Confetti */}
      {showConfetti && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "100%",
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          {particles.map((p, i) => (
            <ConfettiParticle
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              {...p}
            />
          ))}
        </div>
      )}

      {/* Success Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)",
          padding: "48px 24px 60px",
          textAlign: "center",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.08,
            backgroundImage:
              "radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            padding: "14px 16px",
            textAlign: "center",
          }}
        >
          <span
            style={{
              color: "rgba(255,255,255,0.9)",
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: 1,
            }}
          >
            雲端桶裝瓦斯服務
          </span>
        </div>
        <div
          style={{
            animation:
              "scaleIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards",
            display: "inline-block",
            marginTop: 8,
          }}
        >
          <div
            style={{
              width: 84,
              height: 84,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckIcon />
          </div>
        </div>
        <h1
          style={{
            color: "#fff",
            fontSize: 24,
            fontWeight: 700,
            margin: "16px 0 6px",
            animation: show ? "slideUp 0.5s ease forwards" : "none",
            opacity: show ? 1 : 0,
          }}
        >
          訂單修改成功！
        </h1>
        <p
          style={{
            color: "rgba(255,255,255,0.85)",
            fontSize: 14,
            margin: 0,
            animation: show ? "slideUp 0.5s ease 0.1s forwards" : "none",
            opacity: show ? 1 : 0,
          }}
        >
          您的訂單已更新，我們將盡快為您安排配送
        </p>
      </div>

      {/* Step indicator - all done */}
      <div
        style={{
          margin: "-20px 16px 16px",
          background: "#fff",
          borderRadius: 14,
          padding: "14px 20px",
          boxShadow: "0 4px 16px rgba(34,197,94,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          animation: show ? "slideUp 0.4s ease 0.2s forwards" : "none",
          opacity: show ? 1 : 0,
        }}
      >
        <SuccessStepDot label="修改訂單" />
        <div
          style={{
            width: 40,
            height: 2,
            background: "#22C55E",
            margin: "0 4px",
            marginBottom: 18,
          }}
        />
        <SuccessStepDot label="確認內容" />
        <div
          style={{
            width: 40,
            height: 2,
            background: "#22C55E",
            margin: "0 4px",
            marginBottom: 18,
          }}
        />
        <SuccessStepDot current label="完成" />
      </div>

      {/* Order ID */}
      <div
        style={{
          margin: "0 16px 16px",
          background: "#fff",
          borderRadius: 16,
          padding: "18px 20px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
          border: "1px solid #F0FDF4",
          animation: show ? "slideUp 0.4s ease 0.25s forwards" : "none",
          opacity: show ? 1 : 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color: "#999",
                fontWeight: 500,
                marginBottom: 4,
                letterSpacing: 0.5,
              }}
            >
              訂單編號
            </div>
            <div
              style={{
                fontSize: 17,
                fontWeight: 700,
                color: "#333",
                letterSpacing: 0.5,
              }}
            >
              {order.orderId}
            </div>
          </div>
          <button
            type="button"
            onClick={onCopy}
            style={{
              background: copied ? "#22C55E" : "#F0FDF4",
              border: "none",
              borderRadius: 10,
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 600,
              color: copied ? "#fff" : "#16A34A",
              cursor: "pointer",
              transition: "all 0.3s",
            }}
          >
            {copied ? "已複製 ✓" : "複製"}
          </button>
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 16 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "#F0F4FF",
              borderRadius: 20,
              padding: "5px 12px",
              fontSize: 12,
              color: colors.primary,
              fontWeight: 600,
            }}
          >
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: colors.primary,
                animation: "pulse 2s infinite",
              }}
            />
            待瓦斯行確認
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#999",
              display: "flex",
              alignItems: "center",
            }}
          >
            {order.createdAt}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div
        style={{
          padding: "0 16px",
          animation: show ? "slideUp 0.4s ease 0.3s forwards" : "none",
          opacity: show ? 1 : 0,
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: "18px 20px",
            marginBottom: 12,
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            border: "1px solid #F5F5F5",
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: "#333",
              marginBottom: 16,
            }}
          >
            訂單摘要
          </div>
          {order.items.map((item, idx) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "6px 0",
              }}
            >
              <span style={{ fontSize: 14, color: "#555" }}>
                {item.weight} {item.name}
              </span>
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: colors.primary,
                }}
              >
                ×{item.qty}
              </span>
            </div>
          ))}
        </div>

        {/* Delivery summary */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: "18px 20px",
            marginBottom: 12,
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            border: "1px solid #F5F5F5",
          }}
        >
          <InfoRow label="配送地址" value={order.delivery.address} muted={false} />
          <InfoRow
            label="配送方式"
            value={
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <ClockIcon />
                <span style={{ color: colors.primary, fontWeight: 600 }}>
                  {order.delivery.method}
                </span>
              </span>
            }
            muted={false}
          />
          <InfoRow label="預計時間" value={order.delivery.estimatedTime} muted={false} />
          <InfoRow label="聯絡電話" value={order.contact.phone} muted={false} />
        </div>

        {/* Tips */}
        <div
          style={{
            margin: "8px 0 16px",
            padding: "16px 18px",
            background: "#F0FDF4",
            borderRadius: 12,
            border: "1px solid #DCFCE7",
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#166534",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>💡</span> 接下來會發生什麼？
          </div>
          <ul
            style={{
              margin: 0,
              paddingLeft: 18,
              fontSize: 13,
              color: "#166534",
              lineHeight: 1.9,
            }}
          >
            <li>瓦斯行確認後會透過 LINE 通知您</li>
            <li>配送員出發時會再次推播提醒</li>
            <li>如需取消請在配送前聯繫客服</li>
          </ul>
        </div>
      </div>

      {/* Bottom buttons */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          background: "#fff",
          borderTop: "1px solid #F0F0F0",
          padding: "12px 16px",
          paddingBottom: "max(12px, env(safe-area-inset-bottom))",
          display: "flex",
          gap: 12,
          zIndex: 20,
          boxSizing: "border-box",
        }}
      >
        <button
          type="button"
          style={{
            flex: 1,
            padding: "15px",
            borderRadius: 12,
            border: "2px solid #E5E7EB",
            background: "#fff",
            color: "#666",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          查看訂單
        </button>
        <button
          type="button"
          onClick={onBackToLine}
          style={{
            flex: 1.5,
            padding: "15px",
            borderRadius: 12,
            border: "none",
            background: "linear-gradient(135deg, #22C55E, #16A34A)",
            color: "#fff",
            fontSize: 15,
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 4px 14px rgba(34,197,94,0.35)",
          }}
        >
          返回 LINE 聊天室
        </button>
      </div>
    </div>
  );
}

function SuccessStepDot({
  current,
  label,
}: {
  current?: boolean;
  label: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: "#22C55E",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 700,
          boxShadow: current ? "0 0 0 4px rgba(34,197,94,0.2)" : "none",
        }}
      >
        ✓
      </div>
      <span
        style={{
          fontSize: 11,
          color: "#22C55E",
          fontWeight: current ? 700 : 500,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    </div>
  );
}

function ExpiredPage({ onBackToLine }: { onBackToLine: () => void }) {
  return (
    <DraftNotFoundView
      onBackToDrafts={onBackToLine}
      title="訂單已過期"
      description="此草稿訂單已失效，請回到 LINE 聊天室重新發起一鍵下單。"
      buttonText="返回 LINE 聊天室重新下單"
      imageUri={OrderExpiredImg}
      imageAlt="訂單已過期"
    />
  );
}

